import noop from 'lodash/noop';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { AriaAttributes } from '../../../constants/AccessibilityConstants';
import {
  ButtonRowButtonSize,
  ButtonRowText,
  ButtonRowType,
  ButtonTextType,
} from '../../../constants/ButtonRow';
import { ContentSections } from '../../../constants/ContentSections';
import { ButtonRowIcons } from '../../../constants/Icon';
import QA from '../../../constants/QATags';
import { createBEM } from '../../../lib/utils/css';
import Icon from '../../Icon/Icon';
import { withVisibilityLogger } from '../../VisibilityLogger/VisibilityLogger';
import IconInteractive from '../IconInteractive/IconInteractive';
import SourceCard from '../SourceCard/SourceCard';

import './ButtonRow.scss';

class ButtonRow extends React.Component {
  static propTypes = {
    pandoraId: PropTypes.string,
    addToMusicOnClick: PropTypes.func,
    deletePlaylistOnClick: PropTypes.func,
    removeFromMusicOnClick: PropTypes.func,
    deleteStationOnClick: PropTypes.func,
    createStationOnClick: PropTypes.func,
    editOnClick: PropTypes.func,
    shuffleOnClick: PropTypes.func,
    playOnClick: PropTypes.func,
    pauseOnClick: PropTypes.func,
    shareOnClick: PropTypes.func,
    stationIconOnClick: PropTypes.func,
    ellipsisSourceCardMenuItems: PropTypes.array,
    stationSourceCardMenuItems: PropTypes.array,
    canDelete: PropTypes.bool,
    buttonTextType: PropTypes.oneOf(values(ButtonTextType)),
    hiddenButtons: PropTypes.arrayOf(PropTypes.oneOf(values(ButtonRowType))),
    disabledButtons: PropTypes.arrayOf(PropTypes.oneOf(values(ButtonRowType))),
    isDark: PropTypes.bool,
    isCentered: PropTypes.bool,
    isSidebar: PropTypes.bool,
    isStation: PropTypes.bool,
    isLarge: PropTypes.bool,
    hasLeftButton: PropTypes.bool,
    doShowEllipsis: PropTypes.bool,
    qaTagGetter: PropTypes.func,
  };

  static defaultProps = {
    hiddenButtons: [],
    disabledButtons: [],
    hasLeftButton: true,
  };

  getQaTag = defaultTag => {
    return this.props.qaTagGetter ? this.props.qaTagGetter(defaultTag) : defaultTag;
  };

  getPauseText = () => {
    switch (this.props.buttonTextType) {
      case ButtonTextType.PLAY_ALBUM:
        return ButtonTextType.PAUSE_ALBUM;
      case ButtonTextType.PLAY_SONG:
        return ButtonTextType.PAUSE_SONG;
      case ButtonTextType.PLAY_PODCAST:
        return ButtonTextType.PAUSE_PODCAST;
      case ButtonTextType.PLAY_EPISODE:
        return ButtonTextType.PAUSE_EPISODE;
      case ButtonTextType.PLAY:
        return ButtonTextType.PAUSE;
      default:
        return ButtonTextType.PAUSE_SONG;
    }
  };

  renderHoverText = text => {
    const className = 'ButtonRow__button__text';
    const itemAriaAttributes = {
      [AriaAttributes.HIDDEN]: true,
    };
    return (
      <div className={className} {...itemAriaAttributes}>
        <div className={`${className}--inner`}>{text}</div>
      </div>
    );
  };

  renderIcon = (type, onClick, text, isDisabled) => {
    const classes = createBEM('ButtonRow__button', {
      [type]: true,
    });
    const buttonSize = ButtonRowButtonSize[type];
    const iconClasses = createBEM('ButtonRow__icon', {
      [buttonSize]: !!buttonSize,
    });
    const glyph = ButtonRowIcons[type];
    return (
      <button type='button' className={classes} onClick={onClick} aria-disabled={isDisabled}>
        <span className='visuallyHidden'>{text}</span>
        {glyph.hover ? (
          <IconInteractive glyph={glyph} isDark={this.props.isDark} />
        ) : (
          <Icon className={iconClasses} glyph={glyph} focusable='false' />
        )}
      </button>
    );
  };

  renderButton = (type, onClick, qaTag, buttonTextType) => {
    const { disabledButtons, hiddenButtons } = this.props;
    if (hiddenButtons.includes(type)) {
      return;
    }
    const className = 'ButtonRow__buttonContainer';
    const textType = buttonTextType ? buttonTextType : type;
    const text = ButtonRowText[textType] ? <FormattedMessage {...ButtonRowText[textType]} /> : null;
    const isDisabled = disabledButtons.includes(type) || null;
    if (onClick && onClick !== noop) {
      const classes = createBEM(className, {
        disable: isDisabled,
      });
      return (
        <div className={classes} data-qa={this.getQaTag(qaTag)}>
          {this.renderIcon(type, onClick, text, isDisabled)}
          {this.renderHoverText(text)}
        </div>
      );
    }
    if (type === ButtonRowType.MORE) {
      const moreClassName = createBEM(className, {
        more: true,
      });

      if (this.props.ellipsisSourceCardMenuItems || isDisabled) {
        const isUnavailable = !this.props.ellipsisSourceCardMenuItems || isDisabled;
        return (
          <div className={moreClassName}>
            <div className={createBEM('ButtonRow__button', { more: true })}>
              <SourceCard
                isEllipsis={true}
                isEllipsisSmall={true}
                isDark={this.props.isDark}
                isButtonRow={true}
                sourceCardMenuItems={this.props.ellipsisSourceCardMenuItems}
                isUnavailable={isUnavailable}
              />
            </div>
            {!isUnavailable && this.renderHoverText(text)}
          </div>
        );
      }
    }
  };

  renderEllipsis = () => {
    const className = 'ButtonRow__buttonContainer';
    if (this.props.ellipsisSourceCardMenuItems) {
      return (
        <div className={className} data-qa={QA.BRELL}>
          <SourceCard sourceCardMenuItems={this.props.ellipsisSourceCardMenuItems} qaTag={QA.BRSC}>
            {this.renderIcon(ButtonRowType.MORE)}
            {this.renderHoverText(<FormattedMessage {...ButtonRowText.more} />)}
          </SourceCard>
        </div>
      );
    }
  };

  renderStationButton = () => {
    if (this.props.stationSourceCardMenuItems) {
      return (
        <div className='ButtonRow__buttonContainer' data-qa={this.getQaTag(QA.BRCS)}>
          <SourceCard sourceCardMenuItems={this.props.stationSourceCardMenuItems} qaTag={QA.BRSC}>
            {this.renderIcon(
              ButtonRowType.CREATE_STATION,
              this.props.stationIconOnClick,
              <FormattedMessage {...ButtonRowText.playStation} />,
            )}
            {this.renderHoverText(<FormattedMessage {...ButtonRowText.playStation} />)}
          </SourceCard>
        </div>
      );
    }
    return this.renderButton(
      ButtonRowType.CREATE_STATION,
      this.props.createStationOnClick,
      QA.BRCS,
      ButtonTextType.PLAY_STATION,
    );
  };

  render() {
    const classes = createBEM('ButtonRow', {
      large: this.props.isLarge,
      dark: this.props.isDark,
      sidebar: this.props.isSidebar,
      centered: this.props.isCentered,
      missingLeftButton: !this.props.hasLeftButton,
    });

    return (
      <div className={classes}>
        {this.renderButton(ButtonRowType.ADD_TO_COLLECTION, this.props.addToMusicOnClick, QA.BRAMC)}
        {this.renderButton(
          ButtonRowType.REMOVE_FROM_COLLECTION,
          this.props.removeFromMusicOnClick,
          QA.BRRMC,
        )}
        {this.renderButton(
          ButtonRowType.REMOVE_FROM_COLLECTION,
          this.props.deleteStationOnClick,
          QA.BRDS,
        )}
        {this.renderButton(ButtonRowType.EDIT, this.props.editOnClick)}
        {this.renderButton(
          ButtonRowType.DELETE_PLAYLIST,
          this.props.deletePlaylistOnClick,
          QA.BRDP,
        )}
        {this.renderButton(ButtonRowType.SHUFFLE, this.props.shuffleOnClick)}
        {this.renderStationButton()}
        {this.renderButton(
          ButtonRowType.PLAY,
          this.props.playOnClick,
          QA.BRP,
          this.props.buttonTextType,
        )}
        {this.renderButton(
          ButtonRowType.PAUSE,
          this.props.pauseOnClick,
          QA.BRPU,
          this.getPauseText(),
        )}
        {this.renderButton(ButtonRowType.SHARE, this.props.shareOnClick, QA.BRS)}
        {this.props.doShowEllipsis ? this.renderEllipsis() : this.renderButton(ButtonRowType.MORE)}
      </div>
    );
  }
}

export const ButtonRowWithLogger = withVisibilityLogger(
  { contentSection: ContentSections.CONTROL_BAR },
  { useMemo: true },
)(ButtonRow);

export default ButtonRow;
